import { useCallback, useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

/**
 * Blocks navigation with a window.confirm popup if isDirty is true.
 * Usage:
 *   const { confirm } = useBlockUnsavedChanges({ isDirty, message, popstateActiveRef });
 */
export function useBlockUnsavedChanges(
  isDirty = false,
  popstateActiveRef?: React.RefObject<boolean>,
  message = 'You have unsaved changes. Are you sure you want to leave?',
  onConfirm: () => void = () => {},
  onCancel: () => void = () => {},
) {
  const blocker = useBlocker(isDirty);

  const confirm = useCallback(() => {
    if (!isDirty) return Promise.resolve(true);
    // If popstateActiveRef is true, auto-allow navigation (suppress popup)
    if (popstateActiveRef?.current) {
      onConfirm?.();
      return Promise.resolve(true);
    }
    return new Promise<boolean>((resolve) => {
      const confirmed = window.confirm(message);
      if (confirmed) {
        resolve(true);
        onConfirm?.();
      } else {
        resolve(false);
        onCancel?.();
      }
    });
  }, [isDirty, message, onConfirm, onCancel, popstateActiveRef]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      // If popstateActiveRef is true, auto-allow navigation
      if (popstateActiveRef?.current) {
        blocker.proceed();
        return;
      }
      confirm().then((result) => {
        if (result) blocker.proceed();
        else blocker.reset();
      });
    }
  }, [blocker, confirm, popstateActiveRef]);

  return {
    confirm,
  };
}
